import { useState, useEffect } from 'react';
import { Info, X, BookOpen } from 'lucide-react';

/**
 * Generic dismissible "What is this page?" callout. Used across the
 * app (banking, POS, menu items, etc.) to give first-time users a
 * one-paragraph orientation without leaving the page. Once the user
 * clicks the × it stays hidden in this browser (localStorage keyed
 * by `storageKey`), but they can re-summon it from the inline link.
 *
 * Migration note: previously lived at `components/banking/BankingInfoCallout.jsx`.
 * The data-testid is `page-info-{storageKey}` (unified across the app).
 *
 * Props:
 *   storageKey  — unique key (e.g. "banking-rules", "pos", "menu-items")
 *   title       — short heading
 *   children    — body content (paragraphs, lists, etc.)
 *   onTour      — optional callback. If provided, a "Show me around"
 *                 button appears that triggers the parent's tour modal.
 *   tone        — 'violet' (default) | 'sky' | 'emerald' so different
 *                 areas of the app can feel visually distinct.
 */
export function PageInfoCallout({ storageKey, title, children, onTour, tone = 'violet', defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    try {
      // Keep backward-compat with the legacy `banking-info-...` storage key
      // so users who already dismissed the banking callouts don't see them
      // re-appear after this rename.
      const legacy = localStorage.getItem(`banking-info-${storageKey}-dismissed`);
      const current = localStorage.getItem(`page-info-${storageKey}-dismissed`);
      if (legacy === '1' || current === '1') setOpen(false);
    } catch { /* ignore */ }
  }, [storageKey]);

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(`page-info-${storageKey}-dismissed`, '1'); } catch { /* ignore */ }
  };
  const reopen = () => {
    setOpen(true);
    try {
      localStorage.removeItem(`page-info-${storageKey}-dismissed`);
      localStorage.removeItem(`banking-info-${storageKey}-dismissed`);
    } catch { /* ignore */ }
  };

  // Tone palette — each variant uses tailwind utility classes that exist
  // statically (so PurgeCSS keeps them) rather than interpolated strings.
  const toneMap = {
    violet: {
      bg: 'bg-violet-50/50 dark:bg-violet-950/10',
      border: 'border-violet-200 dark:border-violet-900',
      icon: 'text-violet-600',
      title: 'text-violet-900 dark:text-violet-200',
      body: 'text-violet-800/90 dark:text-violet-300/90',
      tour: 'text-violet-700 hover:text-violet-900',
      dismiss: 'text-violet-600/60 hover:text-violet-900',
      hoverShow: 'hover:text-violet-600',
    },
    sky: {
      bg: 'bg-sky-50/50 dark:bg-sky-950/10',
      border: 'border-sky-200 dark:border-sky-900',
      icon: 'text-sky-600',
      title: 'text-sky-900 dark:text-sky-200',
      body: 'text-sky-800/90 dark:text-sky-300/90',
      tour: 'text-sky-700 hover:text-sky-900',
      dismiss: 'text-sky-600/60 hover:text-sky-900',
      hoverShow: 'hover:text-sky-600',
    },
    emerald: {
      bg: 'bg-emerald-50/50 dark:bg-emerald-950/10',
      border: 'border-emerald-200 dark:border-emerald-900',
      icon: 'text-emerald-600',
      title: 'text-emerald-900 dark:text-emerald-200',
      body: 'text-emerald-800/90 dark:text-emerald-300/90',
      tour: 'text-emerald-700 hover:text-emerald-900',
      dismiss: 'text-emerald-600/60 hover:text-emerald-900',
      hoverShow: 'hover:text-emerald-600',
    },
  };
  const t = toneMap[tone] || toneMap.violet;

  if (!open) {
    return (
      <div className="flex justify-end">
        <button
          type="button"
          onClick={reopen}
          className={`text-[10px] text-muted-foreground/70 ${t.hoverShow} flex items-center gap-1`}
          data-testid={`page-info-show-${storageKey}`}
        >
          <Info size={11} /> Show help
        </button>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border ${t.border} ${t.bg} p-3.5 text-xs flex items-start gap-3`}
      data-testid={`page-info-${storageKey}`}
    >
      <BookOpen size={15} className={`${t.icon} mt-0.5 shrink-0`} />
      <div className="flex-1 leading-relaxed">
        <strong className={`font-semibold ${t.title}`}>{title}</strong>
        <div className={`mt-1 ${t.body} [&_a]:underline`}>
          {children}
        </div>
        {onTour && (
          <button
            type="button"
            onClick={onTour}
            className={`mt-2 inline-flex items-center gap-1 text-[11px] font-semibold ${t.tour}`}
            data-testid={`page-info-tour-${storageKey}`}
          >
            ▶ Show me around
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        className={`${t.dismiss} -m-1 p-1`}
        title="Hide this help"
        data-testid={`page-info-dismiss-${storageKey}`}
      >
        <X size={13} />
      </button>
    </div>
  );
}
